import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import QS from 'qs'
import I18n from 'translations/i18n'
import { Flex, Button, Spacer, Typography } from '@jisr-hr/ds'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { addMinutes } from 'date-fns'
import { handleResponseErr } from 'utils/apiHelperUtils'
import { ReactComponent as OfficeIcon } from 'assets/images/Microsoft.svg'
import { ReactComponent as GoogleIcon } from 'assets/images/Google__G__Logo 1.svg'
import { Modal } from 'components/global/atoms'
import { labels } from 'utils/whitelabel'
import { Trans } from 'react-i18next'
import { Toastr } from 'components/global/Toastr'

import { mixPanelAnalytics } from 'utils/mixpanel'
import { useDispatch, useSelector } from 'utils/hooks'
import {
  company_subdomains,
  frameSuccessLogin,
  frameSuccessRelogin,
  login,
  validateCompany,
  validateCompanyKsa,
} from 'redux/authUser/actionCreators'
import { ORGANISATION_NAME, isDevelopmentStage, API_ROOT, serverEnvironment } from '../../../env'
import Slug from './Slug'
import LoginForm from './Form'
import VerificationForm from '../components/VerificationForm'
import LoginTemplate from '../components/loginTemplate'
import OrganizationSelection from './components/OrganizationSelection'
import ChangePasswordForm from './components/ChangePasswordForm'

function Login(props) {
  const dispatch = useDispatch()
  const { company, fetching, policies, organizations, is_saml_account_enabled } = useSelector(
    ({ auth }) => auth,
  )
  const { login_attempt_applicable, is_google_captcha_enabled, attempts } = policies ?? {}
  const [state, setState] = useState({
    error: '',
    loginCredentials: {},
    showError: false,
    showSlug: false,
    showSlugs: false,
    show2FAVerificationModal: false,
  })
  const [changePasswordForm, setChangePasswordForm] = useState({})
  const [timerMinutes, setTimerMinutes] = useState(null)
  const [shouldChallangeCaptcha, setshouldChallangeCaptcha] = useState(false)
  const [isCaptchaRendered, setIsCaptchaRendered] = useState(false)
  const [loginValues, setLoginValues] = useState({})
  const history = useHistory()
  let timer = null

  const { search: searchParams } = useLocation()
  const { from } = QS.parse(searchParams, {
    ignoreQueryPrefix: true,
  })

  const domainSplit = window.location.hostname.split('.')
  const domain = domainSplit.splice(1, domainSplit.length).join('.')

  const replaceSubdomain = (newSubdomain) => {
    let url = window.location.hostname
    url = url.split('.')
    if (isDevelopmentStage && !localStorage.getItem('slug')) {
      setState((prev) => ({ ...prev, showSlug: false }))
      return localStorage.setItem('slug', newSubdomain)
    }
    url[0] = newSubdomain
    window.location.hostname = url.join('.')
    return true
  }

  const onSelectSlug = (slug) => {
    document.cookie = `email=${state.loginCredentials?.email}; path=/; domain=.${domain}`
    if (['aws', 'makenhr', 'oci', 'staging'].includes(serverEnvironment)) {
      return replaceSubdomain(slug.slug)
    }

    localStorage.setItem('slug', slug.slug)
    dispatch(validateCompany({ company_name: slug.slug }))
    setState((prev) => ({ ...prev, showSlug: false, showSlugs: false }))
    return undefined
  }

  const replaceSubdomainKsa = (newSubdomain) => {
    let url = window.location.hostname
    url = url.split('.')
    url[0] = newSubdomain
    if (!url.includes('ksa')) {
      window.location = `//${newSubdomain}.jisr.net.sa`
    }
  }

  const handleReject = (res, company_name) => {
    const newState = {
      error: handleResponseErr(res),
      showError: true,
    }
    if (company_name === 'signin' && !state.error) {
      newState.showError = false
    } else newState.showError = true
    timer = setTimeout(() => setState((prev) => ({ ...prev, ...newState })), 100)
    timer = setTimeout(() => setState((prev) => ({ ...prev, showSlug: true })), 100)
  }

  const handleValidateCompany = (company_name) => {
    if (company_name !== 'signin') {
      dispatch(validateCompany({ company_name }))
        .then(() => replaceSubdomain(company_name))
        .catch(({ response }) => {
          if (labels.name !== 'Jisr') {
            // no need to hit ksa api in whitelabels branches[projects]
            return handleReject({ response }, company_name)
          }

          if (response && response.status === 403) {
            return dispatch(validateCompanyKsa({ company_name }))
              .then(() => replaceSubdomainKsa(company_name))
              .catch((res) => handleReject(res, company_name))
          }

          return false // irrational eslint
        })
    } else {
      timer = setTimeout(() => setState((prev) => ({ ...prev, showSlug: true })), 100)
    }
  }

  const onSubmitEmailToFindSlug = ({ email, otp_code = undefined }, isOrgName) => {
    if (isOrgName) {
      handleValidateCompany(email)
    } else {
      if (otp_code) {
        document.cookie = `otp_code=${otp_code}; path=/; domain=.${domain}`
      }
      dispatch(company_subdomains({ email, otp_code })).then((val) => {
        const res = val.value.data.data

        if (res?.two_factor_required) {
          setTimerMinutes(addMinutes(new Date(), 5))
          setState((prev) => ({
            ...prev,
            show2FAVerificationModal: true,
            loginCredentials: { email },
          }))
          return undefined
        }

        if (res.organizations?.length > 1) {
          setState((prev) => ({ ...prev, showSlugs: true, show2FAVerificationModal: false }))
        }
        if (res.organizations?.length === 1) {
          setState((prev) => ({ ...prev, show2FAVerificationModal: false }))
          onSelectSlug(res.organizations[0])
        }
        return undefined
      })
    }
  }

  const handleLoginFrameResponse = (e) => {
    if (e.data.source === 'loginFrame' && e.data.payload && e.data.payload.success) {
      localStorage.setItem('LoggedIn', true)
      if (e.data.payload?.data?.is_cookies === false) {
        dispatch(frameSuccessRelogin({ access_token: e.data.payload?.data?.access_token }))
      } else {
        dispatch(frameSuccessLogin({ data: e.data.payload }))
      }
    }
  }

  useEffect(() => {
    handleValidateCompany(ORGANISATION_NAME)
    localStorage.removeItem('suspended_token')
    localStorage.removeItem('LoggedIn')
    localStorage.removeItem('slug')
    window.addEventListener('message', handleLoginFrameResponse)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('message', handleLoginFrameResponse)
    }
  }, [])

  useEffect(() => {
    const passwordAgeToken = localStorage.getItem('password-age-token')
    if (passwordAgeToken) {
      history.replace(`/change_password?token=${passwordAgeToken}&type=reset_password`)
    }
  }, [])

  useEffect(() => {
    const shouldChallange = login_attempt_applicable && is_google_captcha_enabled
    setshouldChallangeCaptcha(shouldChallange)
    setIsCaptchaRendered(false)
  }, [login_attempt_applicable, is_google_captcha_enabled, attempts])

  const handleOtherSSOLogin = (providerName) => {
    window.open(
      `${API_ROOT}/auth?slug=${ORGANISATION_NAME}&type=web&locale=en&provider_name=${providerName}`,
      'jisrLogin',
      'width=560,height=340,toolbar=0,menubar=0,location=0',
    )
  }

  let windowObjectReference = null

  const handleSSOLogin = () => {
    if (!is_saml_account_enabled) {
      Toastr.error(I18n.t('sso_disabled_err'))
      return
    }
    if (windowObjectReference === null || windowObjectReference.closed) {
      windowObjectReference = window.open(
        `${API_ROOT}/saml/init?type=web&&slug=${company?.slug}`,
        undefined,
        'width=560,height=340,toolbar=1,menubar=0,location=0',
      )
    } else {
      windowObjectReference.focus()
    }
  }

  const loginFlow = (values, captchaToken) => {
    setIsCaptchaRendered(false)
    dispatch(login({ type: 'Web', ...values, captcha_token: captchaToken }))
      .then(({ value }) => {
        const { pathname, search = '' } = props.location.state
          ? props.location.state.from
          : { pathname: '/' }
        if (value.data.data.suspended_token) {
          localStorage.setItem('suspended_token', value.data.data.suspended_token)
          return history.replace('/billing_details')
        }
        if (value.data.data.password_reset_token) {
          return history.replace(
            `/change_password?token=${value.data.data.password_reset_token}&type=reset_password`,
          )
        }
        if (value.data.data.two_factor_required) {
          setTimerMinutes(addMinutes(new Date(), 5))
          setState((prev) => ({
            ...prev,
            show2FAVerificationModal: true,
            loginCredentials: values,
          }))
          return true
        }

        if (from === 'foodics') {
          return history.replace('/setting/integration?from=foodics')
        }
        mixPanelAnalytics({ event_name: 'Dashboard_Viewed' })
        return history.replace(pathname + search)
      })
      .catch((e) => {
        if (e.response?.data?.required_change_password) {
          setChangePasswordForm(values)
        }
        if (e?.response?.status === 409) {
          setLoginValues(values)
          setshouldChallangeCaptcha(true)
          setIsCaptchaRendered(true)
        }
      })
  }

  const handleSubmit = (values) => {
    if (shouldChallangeCaptcha) {
      setIsCaptchaRendered(true)
      setLoginValues(values)
      return
    }

    if (values.email) {
      onSubmitEmailToFindSlug(values)
    } else {
      loginFlow(values)
    }
  }

  const handleSlug = () => {
    if (state.showSlugs) {
      return (
        <OrganizationSelection
          onSelectSlug={onSelectSlug}
          slugs={organizations}
          onClickGoBack={() => setState((prev) => ({ ...prev, showSlugs: false }))}
        />
      )
    } else if (state.showSlug) {
      return (
        <Slug
          error={state.error}
          showError={state.showError}
          fetching={fetching}
          onSubmit={onSubmitEmailToFindSlug}
        />
      )
    }
    return undefined
  }

  return (
    <LoginTemplate
      company={company}
      hideHeader={state.showSlug}
    >
      {state.showSlugs || state.showSlug ? (
        handleSlug()
      ) : (
        <div>
          <LoginForm
            onSubmit={handleSubmit}
            fetching={fetching}
            history={history}
            showCaptcha={isCaptchaRendered}
            onCaptchaChange={(captchaToken) => loginFlow(loginValues, captchaToken)}
          />
          <Spacer height="20px" />
          <div className="optional--login-box">
            <hr />
            <span>{I18n.t('OR')}</span>
          </div>
          <Spacer height="20px" />
          <Flex
            itemsCenter
            justifyStart
            flexWrap
            style={{ gap: 12, width: '100%' }}
          >
            <Button
              onClick={() => handleOtherSSOLogin('microsoft')}
              className="flexgrow--centred-btn"
              size="md"
              variant="secondary"
              label={I18n.t('login_with_office365')}
              iconLeading={<OfficeIcon />}
            />
            {labels.name !== 'Maken' && (
              <Button
                onClick={() => handleOtherSSOLogin('google')}
                className="flexgrow--centred-btn"
                size="md"
                variant="secondary"
                label={I18n.t('google')}
                iconLeading={<GoogleIcon />}
              />
            )}
            {labels.name !== 'Maken' && (
              <Button
                onClick={handleSSOLogin}
                className="flexgrow--centred-btn"
                size="md"
                variant="secondary"
                label={I18n.t('sso_login', { name: 'SSO' })}
              />
            )}
          </Flex>
          <Spacer height="16px" />
          {shouldChallangeCaptcha &&
            (attempts <= 0 ? (
              <Typography
                variant="subheading"
                style={{ textAlign: 'justify' }}
                textColor="color/fg/danger/default"
                text={
                  <Trans
                    i18nKey="customKey"
                    defaults={`${I18n.t(
                      'exceeded_tallowed_attempts',
                    )} <span><link>{{linkText}}</link></span>`}
                    values={{ linkText: I18n.t('change_password') }}
                    components={{
                      link: <Link to="/forgot_password" />,
                      span: <span style={{ color: 'var(--ds-core-color-primary-600)' }} />,
                    }}
                  />
                }
              />
            ) : null)}
        </div>
      )}

      <Modal
        title={I18n.t('jisr_login_authentication', {
          name: labels.translation_name,
        })}
        open={state.show2FAVerificationModal}
        handleClose={() => {}}
        fetching={fetching}
        size="sm"
        noPadding
        hideClose
      >
        <VerificationForm
          initialValues={state.loginCredentials}
          timerMinutes={timerMinutes}
          onSubmit={handleSubmit}
        />
      </Modal>
      <ChangePasswordForm
        data={changePasswordForm}
        onClose={() => setChangePasswordForm({})}
      />
    </LoginTemplate>
  )
}

Login.propTypes = {
  location: PropTypes.shape(),
}

export default Login
